import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button } from "@/core/shared/v2/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/core/shared/v2/ui/dialog";
import { useGetAvailableSlots } from "../../api/get-available-slots.query";
import { useSelectTimeSlot } from "../../api/select-time-slot.mutation";
import { useConfigStore } from "../../store/use-config";
import { useOrderingStore } from "../../store/use-ordering-store";
import { TimeSelectForm, type TimeSelectFormValues } from "../time-select-form";

type SelectTimeSlotModalProps = {
  open: boolean;
  onClose: () => void;
};
export const SelectTimeSlotModal = ({ open, onClose }: SelectTimeSlotModalProps) => {
  const { config } = useConfigStore();
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();
  const { timeSlot, selectTimeSlot } = useOrderingStore();
  const { mutate: selectTimeSlotMutation, isLoading } = useSelectTimeSlot({ uuid });
  const { data } = useGetAvailableSlots({
    config: {
      enabled: open,
    },
  });

  const handleTimeChange = useCallback(
    (time: TimeSelectFormValues["time"]) => {
      selectTimeSlot(time ? new Date(time) : undefined);
    },
    [selectTimeSlot],
  );
  const onSubmit = () => {
    if (!timeSlot || !config?.groupActionPath) return;
    selectTimeSlotMutation({ time: timeSlot.toISOString() });
    onClose();
  };

  if (!config) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="max-h-[95vh] w-full max-w-[776px]">
        <DialogHeader>
          <DialogTitle className="text-center font-heading font-semibold text-xl md:text-2xl">
            {t("GROUP_ACTIONS_ORDERING.SELECT_TIME_SLOT_MODAL.HEADER")}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-8 mb-6">
          <TimeSelectForm
            availableSlots={data?.availableSlots}
            defaultTimeSlot={timeSlot}
            onTimeChange={handleTimeChange}
          />
        </div>
        <div className="mt-8 flex justify-center">
          <Button
            variant="primary"
            size="lg"
            onClick={onSubmit}
            className="w-full px-10 md:w-1/3"
            loading={isLoading}
            disabled={!timeSlot}
          >
            {t("GROUP_ACTIONS_ORDERING.SELECT_TIME_SLOT_MODAL.SUBMIT")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
